import { MULTIPART_PART_BYTES } from "./limits.js";
import type {
  User,
  ApiError,
  FilePage,
  Invite,
  LinkMode,
  MeResponse,
  MultipartPart,
  PrivacySettings,
  SaucerFile,
  UploadTicket,
  Visibility,
} from "./api.js";

/** Sent on every request: cookies in the browser, streaming bodies in Node. */
const EXTRA_INIT = { credentials: "include", duplex: "half" } as Record<string, unknown>;

export class SaucerApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "SaucerApiError";
  }
}

export interface UploadOptions {
  filename: string;
  /** Claim a specific link name instead of one derived from the filename. */
  slug?: string;
  linkMode?: LinkMode;
  /** Omitted means the account's default, set with `saucer privacy`. */
  visibility?: Visibility;
  /** Lock the link: nobody sees the file without this password. */
  password?: string;
  onProgress?: (uploaded: number, total: number) => void;
  signal?: AbortSignal;
}

/**
 * One client for the browser and the CLI. Both sides hand it a Blob — the
 * browser a `File`, Node an `fs.openAsBlob()` handle — so the chunking logic
 * for large uploads only exists once.
 */
export class SaucerClient {
  constructor(
    private readonly origin: string,
    private readonly token?: string,
  ) {}

  async me(): Promise<MeResponse> {
    return this.request<MeResponse>("GET", "/api/auth/me");
  }

  /** The edge-only session probe: answers from the KV auth cache, no D1 hop. */
  async session(): Promise<{ user: User | null }> {
    return this.request<{ user: User | null }>("GET", "/api/auth/session");
  }

  async listFiles(before?: string): Promise<FilePage> {
    const query = before ? `?before=${encodeURIComponent(before)}` : "";
    return this.request<FilePage>("GET", `/api/files${query}`);
  }

  async updateFile(
    id: string,
    changes: { slug?: string; linkMode?: LinkMode; visibility?: Visibility },
  ) {
    return this.request<SaucerFile>("PATCH", `/api/files/${id}`, changes);
  }

  async getPrivacy(): Promise<PrivacySettings> {
    return this.request<PrivacySettings>("GET", "/api/auth/settings");
  }

  async setPrivacy(defaultVisibility: Visibility): Promise<PrivacySettings> {
    return this.request<PrivacySettings>("PATCH", "/api/auth/settings", { defaultVisibility });
  }

  async deleteFile(id: string): Promise<void> {
    await this.request<null>("DELETE", `/api/files/${id}`);
  }

  async listInvites() {
    return this.request<{ invites: Invite[] }>("GET", "/api/invites");
  }

  async createInvites(count: number, note?: string) {
    return this.request<{ codes: string[] }>("POST", "/api/invites", { count, note });
  }

  async upload(blob: Blob, options: UploadOptions): Promise<SaucerFile> {
    const ticket = await this.request<UploadTicket>("POST", "/api/uploads", {
      filename: options.filename,
      size: blob.size,
      contentType: blob.type || undefined,
      slug: options.slug,
      linkMode: options.linkMode ?? "preview",
      visibility: options.visibility,
      password: options.password,
    });

    try {
      return ticket.strategy === "direct"
        ? await this.uploadDirect(ticket, blob, options)
        : await this.uploadInParts(ticket, blob, options);
    } catch (error) {
      // Leave no half-finished upload holding on to a link name.
      await this.request("POST", `/api/uploads/${ticket.fileId}/abort`).catch(() => undefined);
      throw error;
    }
  }

  private async uploadDirect(
    ticket: Extract<UploadTicket, { strategy: "direct" }>,
    blob: Blob,
    options: UploadOptions,
  ): Promise<SaucerFile> {
    const response = await fetch(ticket.uploadUrl, {
      method: "PUT",
      headers: this.headers({ "content-type": blob.type || "application/octet-stream" }),
      body: blob,
      signal: options.signal,
      // `credentials` is a browser concept and `duplex` a Node one; neither is
      // in the Workers RequestInit, which also compiles this file.
      ...(EXTRA_INIT as RequestInit),
    });
    const file = await this.parse<SaucerFile>(response);
    options.onProgress?.(blob.size, blob.size);
    return file;
  }

  private async uploadInParts(
    ticket: Extract<UploadTicket, { strategy: "multipart" }>,
    blob: Blob,
    options: UploadOptions,
  ): Promise<SaucerFile> {
    const parts: MultipartPart[] = [];
    let uploaded = 0;

    for (const { partNumber, url } of ticket.parts) {
      const start = (partNumber - 1) * (ticket.partSize || MULTIPART_PART_BYTES);
      const chunk = blob.slice(start, start + ticket.partSize);
      const response = await fetch(url, { method: "PUT", body: chunk, signal: options.signal });
      if (!response.ok) {
        throw new SaucerApiError(response.status, "upload_failed", `Part ${partNumber} failed.`);
      }
      const etag = response.headers.get("etag");
      if (!etag) throw new SaucerApiError(502, "upload_failed", `Part ${partNumber} had no ETag.`);

      parts.push({ partNumber, etag });
      uploaded += chunk.size;
      options.onProgress?.(uploaded, blob.size);
    }

    return this.request<SaucerFile>("POST", `/api/uploads/${ticket.fileId}/complete`, { parts });
  }

  private headers(extra: Record<string, string> = {}): Record<string, string> {
    return this.token ? { ...extra, authorization: `Bearer ${this.token}` } : extra;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const response = await fetch(`${this.origin}${path}`, {
      method,
      headers: this.headers(body === undefined ? {} : { "content-type": "application/json" }),
      body: body === undefined ? undefined : JSON.stringify(body),
      ...(EXTRA_INIT as RequestInit),
    });
    return this.parse<T>(response);
  }

  private async parse<T>(response: Response): Promise<T> {
    if (response.status === 204) return null as T;
    const text = await response.text();
    const payload = text ? (JSON.parse(text) as unknown) : null;

    if (!response.ok) {
      const error = payload as ApiError | null;
      throw new SaucerApiError(
        response.status,
        error?.error ?? "unknown",
        error?.message ?? `Request failed with ${response.status}.`,
      );
    }
    return payload as T;
  }
}
